import { callExternalApi, CallResult } from '../callExternalApi';

export type NewLocationEntry = {
  address: string;
  locationCode?: string;
  locationName?: string;
  floorSuiteRoom?: string;
};

export type ServiceRequest = {
  id?: string;
  created_at?: string;
  requested_by?: string;
  opportunity_id?: string | null;
  request_type: string;
  service_type: string;
  locations: string[];
  new_locations: NewLocationEntry[];
  preferred_term?: string;
  details: string;
  asset_id?: string;
  asset_ids?: string[];
  advanced_fields_type?: string;
  advanced_fields?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

type ServiceRequestBackendResponse = {
  id: string;
  created_at: string;
  requested_by: string | null;
  opportunity_id: string | null;
  request_data: Omit<ServiceRequest, 'id' | 'created_at' | 'requested_by' | 'opportunity_id'>;
};

const normalizeNewLocations = (entries: (string | NewLocationEntry)[]): NewLocationEntry[] =>
  entries.map((loc) => (typeof loc === 'string' ? { address: loc } : loc));

const transformResponse = (res: ServiceRequestBackendResponse): ServiceRequest => ({
  id: res.id,
  created_at: res.created_at,
  requested_by: res.requested_by ?? undefined,
  opportunity_id: res.opportunity_id ?? null,
  ...res.request_data,
  new_locations: normalizeNewLocations(res.request_data.new_locations ?? [])
});

export const getServiceRequests = async (accountIds?: string[]): Promise<CallResult<ServiceRequest[]>> => {
  if (!accountIds?.length) {
    return { data: null, error: { message: 'Must provide account IDs to fetch service request' } };
  }

  const url = `api/service_requests?account_ids=${accountIds.join(',')}`;
  const resp = await callExternalApi<ServiceRequestBackendResponse[]>({
    url,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
  if (resp.data) {
    return { ...resp, data: resp.data.map(transformResponse) };
  }
  return { ...resp, data: null };
};

export async function postServiceRequest(requestBody: ServiceRequest, accountId: string | undefined) {
  const url = 'api/service_requests';
  const resp = await callExternalApi<ServiceRequestBackendResponse>({
    url,
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      AccountScope: accountId
    },
    data: { service_request: requestBody }
  });
  if (resp.data) {
    return { ...resp, data: transformResponse(resp.data) };
  }
  return { ...resp, data: null };
}
